import React, { Fragment, useContext } from 'react';
import { Toolbar, Divider, List, ListItemButton, ListItemIcon, ListItemText, SvgIcon, Box, Typography } from '@mui/material';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import * as Styled from './StyledComponents';
import { FmtpPage } from '../types';
import { SideNavContext } from 'src/components/common/NavigationMenu/SideNav/SideNavContext';
import { useFmtpNavigate } from 'src/hooks/useFmtpNavigate';

export interface SideNavProps {
    selectedNavItem?: string;
    navLinks?: FmtpPage[];
    sideNavLinks?: FmtpPage[];
    hidden?: boolean;
    toggleDrawer: () => void;
    isDrawerOpen: boolean;
    drawerWidth?: number;
    logoutHandler: () => void;
    userDisplayName: string;
    useDesktopSideNav?: boolean;
}

export default function SideNav({
    navLinks,
    toggleDrawer,
    isDrawerOpen,
    drawerWidth,
    userDisplayName,
    logoutHandler,
    selectedNavItem,
    useDesktopSideNav = false,
}: SideNavProps) {
    const { selectedSideNavItem, setSelectedSideNavItem, sideNavPages, setSideNavPages } = useContext(SideNavContext);
    const fmtpNavigate = useFmtpNavigate();

    const handleNavClick = (linkItem: FmtpPage) => {
        if (linkItem.subPages) {
            setSideNavPages(linkItem.subPages);
            setSelectedSideNavItem('');
        }
        fmtpNavigate(linkItem.href);
    };

    return (
        <>
            <nav>
                <Styled.Drawer
                    variant="permanent"
                    open={isDrawerOpen}
                    hidden={!sideNavPages.length || !useDesktopSideNav}
                    drawerWidth={drawerWidth}
                >
                    <List component="nav">
                        <ListItemButton onClick={toggleDrawer} disableRipple={true}>
                            <ListItemIcon id="drawer-action-icon">
                                {isDrawerOpen ? <KeyboardDoubleArrowLeftIcon /> : <KeyboardDoubleArrowRightIcon />}
                            </ListItemIcon>
                            <ListItemText />
                        </ListItemButton>
                        <Divider />
                        {sideNavPages?.map((linkItem) => {
                            return (
                                <ListItemButton
                                    key={linkItem.name}
                                    selected={selectedSideNavItem === linkItem.name}
                                    id={linkItem.name}
                                    disableRipple={true}
                                    onClick={() => handleNavClick(linkItem)}
                                >
                                    {linkItem.icon && (
                                        <ListItemIcon>
                                            <SvgIcon component={linkItem.icon}></SvgIcon>
                                        </ListItemIcon>
                                    )}
                                    <ListItemText
                                        primary={linkItem.displayText}
                                        className={`drawer-label ${selectedSideNavItem === linkItem.name ? 'selected' : ''}`}
                                    />
                                </ListItemButton>
                            );
                        })}
                    </List>
                </Styled.Drawer>

                <Styled.MobileDrawer
                    id="mobile-drawer"
                    container={window !== undefined ? () => window.document.body : undefined}
                    variant="temporary"
                    open={isDrawerOpen}
                    onClose={toggleDrawer}
                    ModalProps={{
                        keepMounted: true, // Better open performance on mobile.
                    }}
                >
                    <Box onClick={toggleDrawer}>
                        <List component="nav">
                            <Typography id="mobile-username" component="h6" variant="subtitle1" color="default" noWrap>
                                {userDisplayName}
                            </Typography>
                            <ListItemButton onClick={logoutHandler} id={'mobile-logout'} disableRipple={true}>
                                <ListItemText primary="Logout" />
                            </ListItemButton>
                            <Divider />
                            {navLinks?.map((linkItem) => {
                                const isSelected = !selectedSideNavItem ? selectedNavItem === linkItem.name : false;
                                return (
                                    <Fragment key={linkItem.name}>
                                        <ListItemButton
                                            onClick={() => handleNavClick(linkItem)}
                                            selected={isSelected}
                                            id={linkItem.name}
                                            disableRipple={true}
                                        >
                                            <ListItemText
                                                primary={linkItem.displayText}
                                                className={isSelected ? 'selected' : ''}
                                            />
                                        </ListItemButton>
                                        {'subPages' in linkItem && linkItem.name === selectedNavItem ? (
                                            sideNavPages?.map((sideNavLinkItem) => {
                                                {
                                                    return !sideNavLinkItem.doNotDisplay ? (
                                                        <Fragment key={sideNavLinkItem.name}>
                                                            <ListItemButton
                                                                selected={selectedSideNavItem === sideNavLinkItem.name}
                                                                id={sideNavLinkItem.name}
                                                                disableRipple={true}
                                                                onClick={() => handleNavClick(sideNavLinkItem)}
                                                            >
                                                                <ListItemText
                                                                    primary={sideNavLinkItem.displayText}
                                                                    className={
                                                                        selectedSideNavItem === sideNavLinkItem.name
                                                                            ? 'selected'
                                                                            : ''
                                                                    }
                                                                />
                                                            </ListItemButton>
                                                        </Fragment>
                                                    ) : (
                                                        <></>
                                                    );
                                                }
                                            })
                                        ) : (
                                            <></>
                                        )}
                                        <Divider />
                                    </Fragment>
                                );
                            })}
                        </List>
                    </Box>
                </Styled.MobileDrawer>
            </nav>
        </>
    );
}
